import MDButton from 'components/MDButton'
import React, { useState, useEffect } from 'react'
import MDModal from 'components/MDModal'
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import MDInput from 'components/MDInput'
import { useAppServices } from 'hook/services'
import { Stack, Grid, TextField, MenuItem, FormControlLabel, Checkbox, Select } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'


const CreateBookingModal = ({ handleRefresh }) => {
  const AppService = useAppServices()

  const [open, setOpen] = useState(false)
  const [processing, setProcessing] = useState(false)

  const [listings, setListings] = useState([])
  const [users, setUsers] = useState([])
  const [selectedListing, setSelectedListing] = useState(null)
  const [selectedUser, setSelectedUser] = useState(null)
  const [loadingListings, setLoadingListings] = useState(false)
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [searchListing, setSearchListing] = useState('')
  const [searchUser, setSearchUser] = useState('')
  const [page] = useState(1);
  const [limit] = useState(20);

  const [formData, setFormData] = useState({
    shootDate: '',
    startTime: '',
    endTime: '',
    numberOfGuests: '',
    basePrice: '',
    extraCharges: '',
    serviceCharges: '',
    taxPrice: '',
    firstName: '',
    lastName: '',
    company: '',
    instagramUserName: '',
    mobile: '',
    purpose: '',
    spaceUsage: '',
    portfolioLink: '',
    specialRequest: '',
    adultContent: false,
    publicLiability: false,
    instagramAccount: false,
    covidSetRequired: false,
    bookingTerm: true,
    seventysevenPercentage: false,
    status: 'Pending',
  })

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const validate = () => {
    if (!selectedListing) { alert('Please select a listing.'); return false }
    if (!selectedUser) { alert('Please select a user.'); return false }
    if (!formData.shootDate) { alert('Please select shoot date.'); return false }
    if (!formData.startTime || !formData.endTime) { alert('Please enter start and end time.'); return false }
    if (!formData.firstName) { alert('Please enter first name.'); return false }
    if (!formData.mobile) { alert('Please enter mobile number.'); return false }
    if (!formData.numberOfGuests) { alert('Please enter number of guests.'); return false }
    if (!formData.basePrice) { alert('Please enter base price.'); return false }
    return true
  }

  const fetchListings = async (searchTerm = "", pageNumber = 1) => {
    setLoadingListings(true);
    try {
      const params = { page: pageNumber, limit, toaster: false, startDate: 'null', endDate: 'null', search: searchTerm?.trim() || '' };
      const query = Object.entries(params).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
      const { response } = await AppService.listing.get({ query });
      if (response?.success) setListings(response.data || []);
      else setListings([]);
    } catch (err) {
      console.error('Error fetching listings:', err);
      setListings([]);
    } finally { setLoadingListings(false); }
  };

  const fetchUsers = async (searchTerm = "", pageNumber = 1) => {
    setLoadingUsers(true);
    try {
      const params = { page: pageNumber, limit, toaster: false, startDate: 'null', endDate: 'null', search: searchTerm?.trim() || '' };
      const query = Object.entries(params).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
      const { response } = await AppService.auth.get({ query });
      if (response?.success) setUsers(response.data || []);
      else setUsers([]);
    } catch (err) {
      console.error(' Error fetching users:', err);
      setUsers([]);
    } finally { setLoadingUsers(false); }
  };

  useEffect(() => { fetchListings(searchListing, page); }, [searchListing, page]);
  useEffect(() => { fetchUsers(searchUser, page); }, [searchUser, page]);



  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return
    setProcessing(true)

    const payload = [
      {
        shootDetail: {
          shootDate: formData.shootDate,
          startTime: formData.startTime,
          endTime: formData.endTime,
          numberOfGuests: formData.numberOfGuests,
          basePrice: formData.basePrice,
          extraCharges: formData.extraCharges || 0,
          serviceCharges: formData.serviceCharges || 0,
          taxPrice: formData.taxPrice || 0,
          duration: `${formData.startTime} - ${formData.endTime}`,
          shootTime: `${formData.startTime}—${formData.endTime}`,
          bookedBy: selectedUser?._id,
          listing: selectedListing?._id,
          id: Date.now().toString(),
        },
        firstName: formData.firstName,
        lastName: formData.lastName,
        company: formData.company,
        instagramUserName: formData.instagramUserName,
        mobile: formData.mobile,
        purpose: formData.purpose,
        spaceUsage: formData.spaceUsage,
        portfolioLink: formData.portfolioLink,
        specialRequest: formData.specialRequest,
        adultContent: formData.adultContent ? 'yes' : 'no',
        publicLiability: formData.publicLiability ? 'yes' : 'no',
        instagramAccount: formData.instagramAccount,
        covidSetRequired: formData.covidSetRequired,
        bookingTerm: formData.bookingTerm,
        seventysevenPercentage: formData.seventysevenPercentage,
        status: formData.status,
        bookingPrice: formData.basePrice,
        serviceCharges: formData.serviceCharges,
        taxPrice: formData.taxPrice,
      },
    ]

    try {
      const { response } = await AppService.booking.create({ payload })
      if (response?.success) {
        alert('Booking created successfully!')
        setOpen(false)
        if (handleRefresh) handleRefresh()
      } else alert(response?.message || 'Booking creation failed.')
    } catch (err) {
      console.error(err)
      alert('Something went wrong while creating booking.')
    }
    setProcessing(false)
  }

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 1200,
    maxHeight: '80vh',
    overflow: 'auto',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
  }

  return (
    <>
      <MDButton color="white" variant="contained" onClick={() => setOpen(true)}>
        Create Booking
      </MDButton>

      <MDModal open={open} onClose={() => setOpen(false)}>
        <MDBox component="form" onSubmit={handleSubmit} sx={style}>
          <MDTypography variant="h5" mb={2}>Create Booking</MDTypography>

          <Stack spacing={2}>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={listings}
                  getOptionLabel={(option) => option.title}
                  value={selectedListing}
                  onChange={(_e, newValue) => setSelectedListing(newValue)}
                  onInputChange={(_e, value) => setSearchListing(value)}
                  renderInput={(params) => <TextField {...params} label="Select Listing" fullWidth />}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={users}
                  getOptionLabel={(option) => option.display_name}
                  value={selectedUser}
                  onChange={(_e, newValue) => setSelectedUser(newValue)}
                  onInputChange={(_e, value) => setSearchUser(value)}
                  renderInput={(params) => <TextField {...params} label="Select User" fullWidth />}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <MDInput type="date" label="Shoot Date" value={formData.shootDate} onChange={(e) => handleChange('shootDate', e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12} md={4}>
                <MDInput type="time" label="Start Time" value={formData.startTime} onChange={(e) => handleChange('startTime', e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12} md={4}>
                <MDInput type="time" label="End Time" value={formData.endTime} onChange={(e) => handleChange('endTime', e.target.value)} fullWidth />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <MDInput type="number" label="Number of Guests" value={formData.numberOfGuests} onChange={(e) => handleChange('numberOfGuests', e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={6}>
                <MDInput type="number" label="Base Price" value={formData.basePrice} onChange={(e) => handleChange('basePrice', e.target.value)} fullWidth />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={6}><MDInput label="First Name" value={formData.firstName} onChange={(e) => handleChange('firstName', e.target.value)} fullWidth /></Grid>
              <Grid item xs={6}><MDInput label="Last Name" value={formData.lastName} onChange={(e) => handleChange('lastName', e.target.value)} fullWidth /></Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={6}><MDInput label="Company" value={formData.company} onChange={(e) => handleChange('company', e.target.value)} fullWidth /></Grid>
              <Grid item xs={6}><MDInput label="Mobile" value={formData.mobile} onChange={(e) => handleChange('mobile', e.target.value)} fullWidth /></Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <MDInput label="Purpose" value={formData.purpose} onChange={(e) => handleChange('purpose', e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={6}>
                <MDInput label="Space Usage" value={formData.spaceUsage} onChange={(e) => handleChange('spaceUsage', e.target.value)} fullWidth />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={4}>
                <MDInput label="Portfolio Link" value={formData.portfolioLink} onChange={(e) => handleChange('portfolioLink', e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={4}>
                <MDInput label="Special Request" value={formData.specialRequest} onChange={(e) => handleChange('specialRequest', e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={4}>
                <Select
                  labelId="status-label"
                  value={formData.status}
                  onChange={(e) => handleChange("status", e.target.value)}
                  fullWidth
                >
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Confirmed">Confirmed</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                </Select>
              </Grid>
            </Grid>


            <Grid container spacing={1}>
              <Grid item>
                <FormControlLabel control={<Checkbox checked={formData.adultContent} onChange={(e) => handleChange('adultContent', e.target.checked)} />} label="Adult Content" />
              </Grid>
              <Grid item>
                <FormControlLabel control={<Checkbox checked={formData.publicLiability} onChange={(e) => handleChange('publicLiability', e.target.checked)} />} label="Public Liability" />
              </Grid>
              <Grid item>
                <FormControlLabel control={<Checkbox checked={formData.instagramAccount} onChange={(e) => handleChange('instagramAccount', e.target.checked)} />} label="Instagram Account" />
              </Grid>
              <Grid item>
                <FormControlLabel control={<Checkbox checked={formData.covidSetRequired} onChange={(e) => handleChange('covidSetRequired', e.target.checked)} />} label="Covid Set Required" />
              </Grid>
              <Grid item>
                <FormControlLabel control={<Checkbox checked={formData.bookingTerm} onChange={(e) => handleChange('bookingTerm', e.target.checked)} />} label="Booking Terms Accepted" />
              </Grid>
              <Grid item>
                <FormControlLabel control={<Checkbox checked={formData.seventysevenPercentage} onChange={(e) => handleChange('seventysevenPercentage', e.target.checked)} />} label="77%" />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
              <MDButton variant="gradient" color="primary" type="button" onClick={() => setOpen(false)}>Close</MDButton>
              <MDButton variant="gradient" color="info" type="submit" loading={processing} disabled={processing}>Create Booking</MDButton>
            </Stack>

          </Stack>
        </MDBox>
      </MDModal>
    </>
  )
}

export default CreateBookingModal
